// src/pages/cadastros/Maquinas/EditarMaquina.jsx
// OBS: Nesta versão, "Máquinas" foi renomeado para "Equipamentos" na UI,
// mas mantemos a coleção "maquinas" para compatibilidade.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import { uploadToStorage } from "../../../utils/storageUpload";
import { calcularProximaDataCamadas } from "../../../utils/preventivaCamadas";

const TIPO_OPTIONS = [
  "Pin Load",
  "Plate Load",
  "Banco",
  "Cross",
  "Guiado",
  "Livre",
  "Esteira",
  "Bike",
  "Spinning",
  "Eliptico",
  "Escada",
  "Remo",
];

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const PERIODICIDADE_ORDEM = ["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"];

function normalizarPeriodicidade(value) {
  const v = String(value || "").trim().toUpperCase();
  return PERIODICIDADE_ORDEM.includes(v) ? v : "";
}

function inferirPeriodicidadeItens(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return "";
  let maxIdx = -1;
  itens.forEach((item) => {
    const camada = normalizarPeriodicidade(item?.periodicidade);
    const idx = PERIODICIDADE_ORDEM.indexOf(camada);
    if (idx > maxIdx) maxIdx = idx;
  });
  return maxIdx >= 0 ? PERIODICIDADE_ORDEM[maxIdx] : "";
}

function montarNomePreventiva(baseNome, periodicidade) {
  const nome = String(baseNome || "").trim();
  const per = String(periodicidade || "").trim();
  if (!per) return nome;
  const upperNome = nome.toUpperCase();
  const upperPer = per.toUpperCase();
  if (upperNome.includes(upperPer)) return nome;
  return `${nome} ${per}`.trim();
}

function normalizeFotos(d) {
  const arr = Array.isArray(d?.fotosUrls) ? d.fotosUrls : Array.isArray(d?.fotos) ? d.fotos : [];
  const legacy = d?.fotoUrl ? [d.fotoUrl] : [];
  const uniq = Array.from(new Set([...legacy, ...arr].filter(Boolean)));
  return uniq;
}

function EditarMaquina() {
  const { id } = useParams();
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const { modoGlobal, empresaAtual } = useEmpresa();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const Layout = isMobile ? LayoutMobile : LayoutDesktop;

  // -------------------- states --------------------
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState("");

// Academias (coleção "setores") vinculadas ao cliente selecionado
const [academias, setAcademias] = useState([]);
const [academiaId, setAcademiaId] = useState("");
const [carregandoAcademias, setCarregandoAcademias] = useState(false);

  // Modelos de Preventiva (vinculo do equipamento)
  const [modelosPreventiva, setModelosPreventiva] = useState([]);
  const [carregandoModelosPreventiva, setCarregandoModelosPreventiva] = useState(false);
  const [filtroModeloPreventiva, setFiltroModeloPreventiva] = useState("");
  const [modeloPreventivaIds, setModeloPreventivaIds] = useState([]);
  const modeloPreventivaIdsOrigRef = useRef([]);

  // Checklists de instalacao
  const [checklistsInstalacao, setChecklistsInstalacao] = useState([]);
  const [carregandoChecklistsInstalacao, setCarregandoChecklistsInstalacao] = useState(false);
  const [checklistInstalacaoAtiva, setChecklistInstalacaoAtiva] = useState(false);
  const [checklistInstalacaoId, setChecklistInstalacaoId] = useState("");
  const checklistInstalacaoAtivaOrigRef = useRef(false);
  const checklistInstalacaoIdOrigRef = useRef("");

  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("");
  const [marca, setMarca] = useState("");
  const [modelo, setModelo] = useState("");
  const [numeroSerie, setNumeroSerie] = useState("");
  const [cor, setCor] = useState("");
  const [instaladoEm, setInstaladoEm] = useState(""); // YYYY-MM-DD
  const [montadoPor, setMontadoPor] = useState("");
  const [condicoesIniciais, setCondicoesIniciais] = useState(3);
  const [descricao, setDescricao] = useState("");

  // Fotos (modelo igual NovaOS: câmera + arquivo + preview + remover)
  const inputFotoCameraRef = useRef(null);
  const inputFotoArquivoRef = useRef(null);
  const [fotos, setFotos] = useState([]); // [{id, kind:'existing'|'new', url?, file?, previewUrl}]

  const condicoesOptions = useMemo(() => [1, 2, 3, 4, 5], []);


  const empresaIdParaAcademias = useMemo(() => {
    if (!usuario) return "";
    if (!modoGlobal && empresaAtual?.id) return empresaAtual.id;
    if (modoGlobal) return clienteId || "";
    return "";
  }, [usuario, modoGlobal, empresaAtual, clienteId]);


  // -------------------- carregar doc e (se necessário) clientes --------------------
  useEffect(() => {
    async function carregar() {
      if (!usuario || !id) return;
      setCarregando(true);
      setErro("");

      try {
        if (modoGlobal) {
          const refClientes = collection(db, "empresas");
          const q = query(refClientes, where("grupoId", "==", usuario.grupoId));
          const snap = await getDocs(q);
          const lista = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
          setClientes(lista);
        }

        const ref = doc(db, "maquinas", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErro("Equipamento não encontrado.");
          setCarregando(false);
          return;
        }

        const d = snap.data();

        setCodigo(d.codigo || "");
        setNome(d.nome || "");
        setTipo(d.tipo || "");
        setMarca(d.marca || "");
        setModelo(d.modelo || "");
        setNumeroSerie(d.numeroSerie || "");
        setCor(d.cor || "");
        setInstaladoEm(d.instaladoEm || "");
        setMontadoPor(d.montadoPor || "");
        setCondicoesIniciais(Number(d.condicoesIniciais ?? 3) || 3);
        setDescricao(d.descricao || "");

        setAcademiaId(d.setorId || d.academiaId || "");

        const idsModelos = Array.isArray(d.preventivasModeloIds) ? d.preventivasModeloIds : [];
        setModeloPreventivaIds(idsModelos);
        modeloPreventivaIdsOrigRef.current = idsModelos;

        setChecklistInstalacaoAtiva(!!d.checklistInstalacaoAtiva || !!d.checklistInstalacaoId);
        setChecklistInstalacaoId(d.checklistInstalacaoId || "");
        checklistInstalacaoAtivaOrigRef.current = !!d.checklistInstalacaoAtiva || !!d.checklistInstalacaoId;
        checklistInstalacaoIdOrigRef.current = d.checklistInstalacaoId || "";

        const urls = normalizeFotos(d);
        setFotos(
          urls.map((url) => ({
            id: uid(),
            kind: "existing",
            url,
            previewUrl: url,
          }))
        );

        if (d.empresaId) setClienteId(d.empresaId);
        else if (!modoGlobal && empresaAtual?.id) setClienteId(empresaAtual.id);
      } catch (e) {
        console.error("Erro ao carregar equipamento:", e);
        setErro("Erro ao carregar equipamento. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [usuario, id, modoGlobal, empresaAtual]);


useEffect(() => {
  async function carregarAcademias() {
    if (!usuario?.grupoId) return;

    if (!empresaIdParaAcademias) {
      setAcademias([]);
      setAcademiaId("");
      return;
    }

    setCarregandoAcademias(true);
    try {
      const ref = collection(db, "setores");
      const q = query(
        ref,
        where("grupoId", "==", usuario.grupoId),
        where("empresaId", "==", empresaIdParaAcademias)
      );
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")
        );

      setAcademias(lista);
      setAcademiaId((prev) => (lista.some((x) => x.id === prev) ? prev : ""));
    } catch (e) {
      console.error("Erro ao carregar academias:", e);
    } finally {
      setCarregandoAcademias(false);
    }
  }

  carregarAcademias();
}, [usuario, empresaIdParaAcademias]);


  useEffect(() => {
    async function carregarModelosPreventiva() {
      if (!usuario?.grupoId) return;

      setCarregandoModelosPreventiva(true);
      try {
        const ref = collection(db, "preventivasModelos");
        const q = query(ref, where("grupoId", "==", usuario.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setModelosPreventiva(lista);
      } catch (e) {
        console.error("Erro ao carregar modelos de preventiva:", e);
      } finally {
        setCarregandoModelosPreventiva(false);
      }
    }

    carregarModelosPreventiva();
  }, [usuario]);

  useEffect(() => {
    async function carregarChecklistsInstalacao() {
      if (!usuario?.grupoId) return;

      setCarregandoChecklistsInstalacao(true);
      try {
        const ref = collection(db, "checklistsModelos");
        const q = query(ref, where("grupoId", "==", usuario.grupoId), where("tipo", "==", "INSTALACAO"));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setChecklistsInstalacao(lista);
      } catch (e) {
        console.error("Erro ao carregar checklists de instalacao:", e);
      } finally {
        setCarregandoChecklistsInstalacao(false);
      }
    }

    carregarChecklistsInstalacao();
  }, [usuario]);

  const modelosPreventivaOrdenados = useMemo(() => {
    const lista = Array.isArray(modelosPreventiva) ? [...modelosPreventiva] : [];
    const marcaN = String(marca || "").trim().toLowerCase();
    const modeloN = String(modelo || "").trim().toLowerCase();

    const score = (m) => {
      const fab = String(m?.fabricante || "").trim().toLowerCase();
      const mod = String(m?.modelo || "").trim().toLowerCase();
      let s = 0;
      if (marcaN && fab === marcaN) s += 2;
      if (modeloN && mod === modeloN) s += 2;
      if (marcaN && modeloN && fab === marcaN && mod === modeloN) s += 5;
      return s;
    };

    lista.sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa !== sb) return sb - sa;
      return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
    });

    return lista;
  }, [modelosPreventiva, marca, modelo]);

  const modelosPreventivaFiltrados = useMemo(() => {
    const t = String(filtroModeloPreventiva || "").trim().toLowerCase();
    if (!t) return modelosPreventivaOrdenados;
    return modelosPreventivaOrdenados.filter((m) => {
      const campos = [m.codigo, m.nome, m.fabricante, m.modelo, m.periodicidade];
      return campos.some((c) => String(c || "").toLowerCase().includes(t));
    });
  }, [modelosPreventivaOrdenados, filtroModeloPreventiva]);


  function anexarFotos(files) {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;

    const novos = arr.map((file) => ({
      id: uid(),
      kind: "new",
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setFotos((prev) => [...prev, ...novos]);
  }

  function removerFoto(idToRemove) {
    setFotos((prev) => {
      const item = prev.find((x) => x.id === idToRemove);
      if (item?.kind === "new" && item?.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch {}
      }
      return prev.filter((x) => x.id !== idToRemove);
    });
  }

  async function resolverCliente() {
    if (!modoGlobal) {
      const empresaId = empresaAtual?.id || null;
      const empresaNome = empresaAtual?.nome || "";
      return { empresaId, empresaNome };
    }

    if (!clienteId) throw new Error("Selecione o cliente.");

    const cLocal = clientes.find((c) => c.id === clienteId);
    if (cLocal) return { empresaId: cLocal.id, empresaNome: cLocal.nome || "" };

    const ref = doc(db, "empresas", clienteId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { empresaId: clienteId, empresaNome: "" };
    const d = snap.data();
    return { empresaId: clienteId, empresaNome: d.nome || "" };
  }

  async function handleSalvar(e) {
    e.preventDefault();

    if (!usuario?.grupoId) {
      setErro("Usuário sem grupo. Não é possível salvar.");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do equipamento.");
      return;
    }

    if (checklistInstalacaoAtiva && !checklistInstalacaoId) {
      setErro("Selecione o checklist de instalacao.");
      return;
    }

if (empresaIdParaAcademias && academias.length > 0 && !academiaId) {
  setErro("Selecione a academia.");
  return;
}

    setSalvando(true);
    setErro("");

    try {
      const { empresaId, empresaNome } = await resolverCliente();

      const prefix = (codigo || `EQ_${nome.trim().slice(0, 20)}`)
        .toString()
        .replace(/\s+/g, "_")
        .slice(0, 40);

      const urlsExistentes = fotos
        .filter((f) => f.kind === "existing" && f.url)
        .map((f) => f.url);

      const novos = fotos.filter((f) => f.kind === "new" && f.file);
      const urlsNovas = [];
      for (let i = 0; i < novos.length; i++) {
        const f = novos[i];
        const url = await uploadToStorage({
          grupoId: usuario.grupoId,
          empresaId: empresaId || "",
          pasta: "equipamentos",
          file: f.file,
          filenamePrefix: `${prefix}_foto${String(i + 1).padStart(2, "0")}`,
        });
        urlsNovas.push(url);
      }

      const fotosUrls = [...urlsExistentes, ...urlsNovas];
      const fotoUrl = fotosUrls?.[0] || "";

      const academiaSelecionada = academias.find((a) => a.id === academiaId) || null;
      const contratoTipoRaw = String(
        academiaSelecionada?.tipoContrato || academiaSelecionada?.tipo || "Avulso"
      ).trim();
      const contratoTipo = contratoTipoRaw || "Avulso";
      const contratoAtivo = !String(contratoTipo).toLowerCase().includes("avulso");

      const checklistInstalacaoSelecionado = checklistsInstalacao.find((c) => c.id === checklistInstalacaoId) || null;
      const checklistAtivoFinal = !!checklistInstalacaoAtiva;
      const checklistIdFinal = checklistAtivoFinal ? (checklistInstalacaoId || "") : "";
      const checklistAtivoOrig = !!checklistInstalacaoAtivaOrigRef.current;
      const checklistIdOrig = String(checklistInstalacaoIdOrigRef.current || "");
      const iniciouNovoChecklistInstalacao = checklistAtivoFinal && !!checklistIdFinal && (
        !checklistAtivoOrig || checklistIdOrig !== String(checklistIdFinal)
      );

      const payload = {
        nome: nome.trim(),
        tipo: (tipo || "").trim(),
        marca: (marca || "").trim(),
        modelo: (modelo || "").trim(),
        numeroSerie: (numeroSerie || "").trim(),
        cor: (cor || "").trim(),
        instaladoEm: instaladoEm || "",
        montadoPor: (montadoPor || "").trim(),
        condicoesIniciais: Number(condicoesIniciais || 3),
        descricao: (descricao || "").trim(),

        fotosUrls,
        fotoUrl, // capa/compatibilidade

        // scopo
        grupoId: usuario.grupoId,
        empresaId: empresaId || "",
        empresaNome: empresaNome || "",

        setorId: academiaId || null,
        setorNome: academiaSelecionada?.nome || "",
        setorCodigo: academiaSelecionada?.codigo || "",
        setorContratoTipo: contratoTipo,

        // ✅ vinculo de modelos de preventivas no equipamento
        preventivasModeloIds: Array.isArray(modeloPreventivaIds) ? modeloPreventivaIds : [],

        // ✅ checklist de instalacao
        checklistInstalacaoAtiva: checklistAtivoFinal,
        checklistInstalacaoId: checklistAtivoFinal ? checklistIdFinal || null : null,
        checklistInstalacaoNome: checklistAtivoFinal ? (checklistInstalacaoSelecionado?.nome || "") : "",

        ...(iniciouNovoChecklistInstalacao
          ? {
              checklistConcluido: false,
              checklistConcluidoEm: null,
              checklistUltimaExecucao: null,
            }
          : {}),

        atualizadoEm: serverTimestamp(),
        atualizadoPor: {
          uid: usuario.uid || "",
          nome: usuario.nome || usuario.email || "",
        },
      };
      const maquinaRef = doc(db, "maquinas", id);

      // --- sincroniza preventivas do equipamento (cria/inativa) ---
      const selecionados = Array.isArray(modeloPreventivaIds) ? modeloPreventivaIds : [];
      const orig = Array.isArray(modeloPreventivaIdsOrigRef.current) ? modeloPreventivaIdsOrigRef.current : [];

      const added = selecionados.filter((x) => !orig.includes(x));
      const removed = orig.filter((x) => !selecionados.includes(x));

      // Busca instâncias atuais da máquina (para evitar duplicar e para inativar)
      const snapPrev = await getDocs(query(collection(db, "preventivas"), where("maquinaId", "==", id)));
      const instancias = snapPrev.docs.map((d) => ({ id: d.id, ...d.data() }));

      const ativosPorModelo = new Set(
        instancias
          .filter((p) => p?.ativo !== false)
          .map((p) => String(p?.modeloPreventivaId || ""))
          .filter(Boolean)
      );

      const modelosMap = new Map((Array.isArray(modelosPreventiva) ? modelosPreventiva : []).map((m) => [m.id, m]));

      const batch = writeBatch(db);

      // 1) atualiza equipamento
      batch.update(maquinaRef, payload);

      // 2) inativa removidos (sem apagar histórico)
      instancias
        .filter((p) => removed.includes(p?.modeloPreventivaId) && p?.ativo !== false)
        .forEach((p) => {
          const ref = doc(db, "preventivas", p.id);
          batch.update(ref, {
            ativo: false,
            inativadoEm: serverTimestamp(),
            inativadoPor: usuario.uid || "",
            atualizadoEm: serverTimestamp(),
          });
        });

      // 2.1) atualiza contrato das preventivas ativas do equipamento
      instancias
        .filter((p) => selecionados.includes(p?.modeloPreventivaId))
        .forEach((p) => {
          const ref = doc(db, "preventivas", p.id);
          const precisaAtivar = contratoAtivo && p?.ativo === false;
          const precisaDesativar = !contratoAtivo && p?.ativo !== false;
          const precisaContrato = String(p?.contratoTipo || "") !== contratoTipo;
          const precisaData = contratoAtivo && !p?.proximaData;
          if (!precisaAtivar && !precisaDesativar && !precisaContrato && !precisaData) return;

          const proximaData = contratoAtivo
            ? calcularProximaDataCamadas({ ...p, contratoTipo }, new Date())
            : null;

          const upd = {
            contratoTipo,
            atualizadoEm: serverTimestamp(),
          };
          if (precisaAtivar) upd.ativo = true;
          if (precisaDesativar) upd.ativo = false;
          if (contratoAtivo && proximaData) upd.proximaData = proximaData;
          if (!contratoAtivo) upd.proximaData = null;

          batch.update(ref, upd);
        });

      // 3) cria os adicionados que ainda não existem
      added
        .filter((mid) => !ativosPorModelo.has(String(mid)))
        .forEach((mid) => {
          const m = modelosMap.get(mid);
          if (!m) return;

          const periodicidadeModelo = m.periodicidade || inferirPeriodicidadeItens(m.itens);
          const prevRef = doc(collection(db, "preventivas"));
          const proximaData = contratoAtivo
            ? calcularProximaDataCamadas({ contratoTipo }, new Date())
            : null;
          batch.set(prevRef, {
            grupoId: usuario.grupoId,

            empresaId: empresaId || "",
            empresaNome: empresaNome || "",

            maquinaId: id,
            maquinaNome: payload.nome,

            setorId: payload.setorId || null,
            setorNome: payload.setorNome || "",
            setorCodigo: payload.setorCodigo || "",

            modeloPreventivaId: m.id,
            modeloPreventivaCodigo: m.codigo || "",

            codigo: m.codigo || "",
            nome: montarNomePreventiva(m.nome || "", periodicidadeModelo),
            fabricante: m.fabricante || "",
            modelo: m.modelo || "",
            periodicidade: periodicidadeModelo || "",
            diaSemana: m.diaSemana ?? null,
            diaMes: m.diaMes ?? null,
            itens: Array.isArray(m.itens) ? m.itens : [],

            contratoTipo,
            proximaData,
            ativo: contratoAtivo,

            criadoEm: serverTimestamp(),
            criadoPor: usuario.uid || "",
            atualizadoEm: serverTimestamp(),
          });
        });

      await batch.commit();

      // Atualiza referência do "original"
      modeloPreventivaIdsOrigRef.current = selecionados;

      navigate("/cadastros/equipamentos");
} catch (e) {
      console.error("Erro ao salvar equipamento:", e);
      setErro(e?.message || "Erro ao salvar equipamento. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <Layout tema={tema} alterarTema={alterarTema} titulo="Editar Equipamento" abaBottom="cadastros">
        <p style={{ padding: 14 }}>Carregando...</p>
      </Layout>
    );
  }

  return (
    <Layout
      tema={tema}
      alterarTema={alterarTema}
      titulo={isMobile ? "Editar Equipamento" : "EDITAR EQUIPAMENTO"}
      subtitulo={"Cadastros / Equipamentos / Editar"}
      abaBottom={isMobile ? "cadastros" : undefined}
    >

      <section style={{ width: "100%" }}>
        <div
          className={isMobile ? "m-card" : "pc-card"}
          style={{
            width: "100%",
            padding: isMobile ? 16 : "16px 40px",
            boxSizing: "border-box",
          }}
        >
          {!isMobile && (
            <header className="pc-table-header">
              <div className="pc-table-header-text">
                <h3>Cadastro de equipamento</h3>
                <p>Atualize os dados do equipamento.</p>
              </div>
            </header>
          )}

          {erro ? <div className="pc-alert pc-alert-erro">{erro}</div> : null}

<form
          onSubmit={handleSalvar}
          className="pc-form"
          style={{
            marginTop: isMobile ? 0 : 12,
            maxWidth: isMobile ? "100%" : 720,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <div className="pc-form-grid">
            {modoGlobal ? (
              <div className="pc-form-group pc-form-group-full">
                <label>Cliente</label>
                <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className="pc-input" required>
                  <option value="">Selecione o cliente</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.id}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

<div className="pc-form-group pc-form-group-full">
  <label>Academia{empresaIdParaAcademias && academias.length > 0 ? " *" : ""}</label>
  <select
    value={academiaId}
    onChange={(e) => setAcademiaId(e.target.value)}
    className="pc-input"
    disabled={!empresaIdParaAcademias || carregandoAcademias}
  >
    <option value="">
      {!empresaIdParaAcademias
        ? "Selecione o cliente primeiro"
        : carregandoAcademias
        ? "Carregando..."
        : academias.length === 0
        ? "Nenhuma academia cadastrada"
        : "Selecione a academia"}
    </option>
    {academias.map((a) => (
      <option key={a.id} value={a.id}>
        {a.nome}
      </option>
    ))}
  </select>
  {empresaIdParaAcademias && !carregandoAcademias && academias.length === 0 ? (
    <small style={{ fontSize: 12, opacity: 0.85 }}>
      Cadastre a academia em Cadastros / Academias para poder vincular o equipamento.
    </small>
  ) : null}
</div>

            <div className="pc-form-group pc-form-group-full">
              <label>Nome *</label>
              <input value={nome} onChange={(e) => setNome(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="pc-input">
                <option value="">Selecione</option>
                {TIPO_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="pc-form-group">
              <label>Marca</label>
              <input value={marca} onChange={(e) => setMarca(e.target.value)} className="pc-input" />
            </div>

            {/* abaixo de Marca */}
            <div className="pc-form-group">
              <label>Modelo</label>
              <input value={modelo} onChange={(e) => setModelo(e.target.value)} className="pc-input" />
            </div>

            {/* preventivas (modelos) */}
            <div className="pc-form-group pc-form-group-full">
              <label>Preventivas (modelos)</label>

              <input
                value={filtroModeloPreventiva}
                onChange={(e) => setFiltroModeloPreventiva(e.target.value)}
                className="pc-input"
                placeholder="Buscar por código, nome, fabricante, modelo..."
                style={{ marginBottom: 8 }}
              />

              <div
                style={{
                  border: "1px solid #334155",
                  borderRadius: 10,
                  padding: 10,
                  maxHeight: 240,
                  overflow: "auto",
                }}
              >
                {carregandoModelosPreventiva ? (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>Carregando modelos...</div>
                ) : modelosPreventivaFiltrados.length === 0 ? (
                  <div style={{ fontSize: 13, opacity: 0.9 }}>
                    Nenhum modelo encontrado. Cadastre em <b>Cadastros / Preventivas</b>.
                  </div>
                ) : (
                  modelosPreventivaFiltrados.map((m) => {
                    const checked = modeloPreventivaIds.includes(m.id);
                    const titulo = `${m.codigo ? `${m.codigo} — ` : ""}${m.nome || "Preventiva"}`;
                    const subt = `${m.fabricante || "-"} • ${m.modelo || "-"} • ${m.periodicidade || "-"}`;
                    return (
                      <label
                        key={m.id}
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "flex-start",
                          padding: "8px 6px",
                          borderBottom: "1px dashed rgba(148,163,184,0.35)",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setModeloPreventivaIds((prev) => {
                              if (prev.includes(m.id)) return prev.filter((x) => x !== m.id);
                              return [...prev, m.id];
                            });
                          }}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ lineHeight: 1.2 }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{titulo}</div>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{subt}</div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>

              <small style={{ fontSize: 12, opacity: 0.85 }}>
                Ao salvar, o sistema cria as preventivas que faltarem e inativa as que forem desmarcadas (sem apagar histórico).
              </small>
            </div>

            {/* checklist de instalacao */}
            <div className="pc-form-group pc-form-group-full">
              <label>Checklist de instalacao</label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={checklistInstalacaoAtiva}
                  onChange={(e) => {
                    const ativo = e.target.checked;
                    setChecklistInstalacaoAtiva(ativo);
                    if (!ativo) setChecklistInstalacaoId("");
                  }}
                />
                Usar checklist de instalacao
              </label>

              {checklistInstalacaoAtiva ? (
                <select
                  className="pc-input"
                  value={checklistInstalacaoId}
                  onChange={(e) => setChecklistInstalacaoId(e.target.value)}
                  disabled={carregandoChecklistsInstalacao}
                  style={{ marginTop: 8 }}
                >
                  <option value="">
                    {carregandoChecklistsInstalacao
                      ? "Carregando..."
                      : checklistsInstalacao.length === 0
                      ? "Nenhum checklist encontrado"
                      : "Selecione o checklist"}
                  </option>
                  {checklistsInstalacao.map((c) => (
                    <option key={c.id} value={c.id}>
                      {`${c.codigo || "-"} — ${c.nome || "Checklist"}`}
                    </option>
                  ))}
                </select>
              ) : null}

              <small style={{ fontSize: 12, opacity: 0.85 }}>
                Cadastre checklists em <b>Cadastros / Checklists</b> (tipo Instalacao).
              </small>
            </div>


            <div className="pc-form-group">
              <label>Número de série / Rastreabilidade</label>
              <input value={numeroSerie} onChange={(e) => setNumeroSerie(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Cor</label>
              <input value={cor} onChange={(e) => setCor(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Instalado</label>
              <input type="date" value={instaladoEm} onChange={(e) => setInstaladoEm(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Montado por</label>
              <input value={montadoPor} onChange={(e) => setMontadoPor(e.target.value)} className="pc-input" />
            </div>

            <div className="pc-form-group">
              <label>Condições iniciais (1 a 5)</label>
              <select value={condicoesIniciais} onChange={(e) => setCondicoesIniciais(Number(e.target.value))} className="pc-input">
                {condicoesOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <small style={{ fontSize: 12, opacity: 0.85 }}>1 = ruim | 5 = excelente</small>
            </div>

            {/* Foto (opcional) - modelo igual NovaOS */}
            <div className="pc-form-group pc-form-group-full">
              <label>Foto (opcional)</label>

              <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button type="button" className="pc-btn-secundario" disabled={salvando} onClick={() => inputFotoCameraRef.current?.click()}>
                  📷 Câmera
                </button>
                <button type="button" className="pc-btn-secundario" disabled={salvando} onClick={() => inputFotoArquivoRef.current?.click()}>
                  ⬆️ Arquivo
                </button>
              </div>

              {/* Inputs escondidos */}
              <input
                ref={inputFotoCameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  anexarFotos(e.target.files);
                  if (e.target) e.target.value = "";
                }}
              />
              <input
                ref={inputFotoArquivoRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  anexarFotos(e.target.files);
                  if (e.target) e.target.value = "";
                }}
              />

              {/* Preview */}
              {fotos.length > 0 ? (
                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {fotos.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: 8,
                        borderRadius: 10,
                        border: "1px solid var(--cor-borda-input, #334155)",
                      }}
                    >
                      <img
                        src={item.previewUrl}
                        alt=""
                        style={{
                          width: 52,
                          height: 52,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid var(--cor-borda-input, #334155)",
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <a href={item.previewUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, opacity: 0.9 }}>
                          Abrir
                        </a>
                        <button
                          type="button"
                          className="pc-btn-perigo"
                          style={{ padding: "4px 8px", fontSize: 12 }}
                          onClick={() => removerFoto(item.id)}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              <small style={{ fontSize: 12, opacity: 0.85, display: "block", marginTop: 8 }}>
                As fotos serão enviadas ao salvar o equipamento.
              </small>
            </div>

            <div className="pc-form-group pc-form-group-full">
              <label>Descrição</label>
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                className="pc-input"
                rows={5}
                placeholder="Observações, detalhes do estado inicial, etc."
              />
            </div>
          </div>

          <div className="pc-form-actions">
            <button type="button" className="pc-btn-secundario" onClick={() => navigate("/cadastros/equipamentos")} disabled={salvando}>
              Cancelar
            </button>
            <button type="submit" className="pc-btn-primario" disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
        </div>
      </section>
</Layout>
  );
}

export default EditarMaquina;
